import PropTypes from 'prop-types'
import { Box, CircularProgress, IconButton, Typography } from '@material-ui/core'
import ThumbUpOutlinedIcon from '@material-ui/icons/ThumbUpOutlined'
import ThumbDownOutlinedIcon from '@material-ui/icons/ThumbDownOutlined'
import ThumbUpIcon from '@material-ui/icons/ThumbUp'
import ThumbDownIcon from '@material-ui/icons/ThumbDown'
import { WA } from './whatsappTheme'

export default function ChatFeedbackPanel({
  submitting = false,
  submitted = false,
  selected = null,
  onSelect,
  onSkip,
  variant = 'customer',
}) {
  if (submitted) {
    return (
      <Box
        sx={{
          px: 2,
          py: variant === 'inline' ? 2 : 4,
          textAlign: 'center',
          bgcolor: '#fff',
          ...(variant === 'prompt' ? { flexGrow: 1 } : {}),
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: WA.text }}>
          Thanks for your feedback
        </Typography>
        <Typography variant="body2" sx={{ color: WA.meta, mt: 0.5 }}>
          We appreciate you taking the time to rate this chat.
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        px: 2,
        py: variant === 'inline' ? 2 : 4,
        textAlign: 'center',
        bgcolor: '#fff',
        ...(variant === 'prompt' ? { flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' } : {}),
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 700, color: WA.text, fontSize: 18 }}>
        Did we help you?
      </Typography>
      <Typography variant="body2" sx={{ color: WA.meta, mt: 0.5, mb: 2.5 }}>
        Your Feedback Matters
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
        <IconButton
          disabled={submitting}
          onClick={() => onSelect?.('up')}
          aria-label="Thumbs up"
          sx={{
            width: 52,
            height: 52,
            bgcolor: selected === 'up' ? 'rgba(2, 125, 194, 0.12)' : '#EEF1F4',
            color: WA.text,
            '&:hover': { bgcolor: 'rgba(2, 125, 194, 0.18)' },
          }}
        >
          {submitting && selected === 'up' ? (
            <CircularProgress size={22} sx={{ color: WA.header }} />
          ) : selected === 'up' ? (
            <ThumbUpIcon />
          ) : (
            <ThumbUpOutlinedIcon />
          )}
        </IconButton>
        <IconButton
          disabled={submitting}
          onClick={() => onSelect?.('down')}
          aria-label="Thumbs down"
          sx={{
            width: 52,
            height: 52,
            bgcolor: selected === 'down' ? 'rgba(2, 125, 194, 0.12)' : '#EEF1F4',
            color: WA.text,
            '&:hover': { bgcolor: 'rgba(2, 125, 194, 0.18)' },
          }}
        >
          {submitting && selected === 'down' ? (
            <CircularProgress size={22} sx={{ color: WA.header }} />
          ) : selected === 'down' ? (
            <ThumbDownIcon />
          ) : (
            <ThumbDownOutlinedIcon />
          )}
        </IconButton>
      </Box>
      {typeof onSkip === 'function' ? (
        <Typography
          variant="caption"
          onClick={submitting ? undefined : onSkip}
          sx={{
            display: 'inline-block',
            mt: 2.5,
            color: WA.meta,
            cursor: submitting ? 'default' : 'pointer',
            textDecoration: 'underline',
          }}
        >
          Skip
        </Typography>
      ) : null}
    </Box>
  )
}

ChatFeedbackPanel.propTypes = {
  submitting: PropTypes.bool,
  submitted: PropTypes.bool,
  selected: PropTypes.oneOf(['up', 'down', null]),
  onSelect: PropTypes.func,
  onSkip: PropTypes.func,
  variant: PropTypes.oneOf(['prompt', 'inline']),
}

export function ChatRatingBadge({ rating, size = 'small' }) {
  if (!rating) return null
  const isUp = rating === 'up' || Number(rating) === 1
  const Icon = isUp ? ThumbUpIcon : ThumbDownIcon
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: size === 'small' ? 0.75 : 1,
        py: 0.25,
        borderRadius: 8,
        bgcolor: isUp ? 'rgba(46, 125, 50, 0.1)' : 'rgba(211, 47, 47, 0.08)',
        color: isUp ? '#2E7D32' : '#C62828',
        fontSize: 11,
        fontWeight: 700,
      }}
      title={isUp ? 'Helpful' : 'Not helpful'}
    >
      <Icon sx={{ fontSize: size === 'small' ? 13 : 16 }} />
      {size !== 'small' ? (isUp ? 'Helpful' : 'Not helpful') : null}
    </Box>
  )
}

ChatRatingBadge.propTypes = {
  rating: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  size: PropTypes.oneOf(['small', 'medium']),
}
